import { getTranslation } from '@easy-expense/intl-client';
import { Icon, IconName } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import React from 'react';
import { Image } from 'react-native';

import { useIsMobile } from '../hooks/useWindow';
import { TEAMS_FAQ_URL } from '../lib/Billing/constants';

const NextStep: React.FC<{ icon: IconName; title: string }> = ({ icon, title }) => (
  <Layout.Row align>
    <Layout.Column
      align
      center
      bg="brandPrimaryXLight"
      radius={12}
      style={{ width: 40, height: 40, flexShrink: 0 }}
    >
      <Icon name={icon} size={20} color={theme.colors.brandPrimary} />
    </Layout.Column>
    <Spacer.Horizontal size={14} />
    <OpenSans.Primary size="s-16" weight="bold-700">
      {title}
    </OpenSans.Primary>
  </Layout.Row>
);

/**
 * Paddle's successUrl for the mobile browser handoff (EZ-5238). Deliberately unauthenticated:
 * the purchaser came from the app and has no web session, so sending them to `/teams/invite`
 * would land them on the login wall this flow exists to avoid.
 */
export const TeamCheckoutSuccessPage: React.FC = () => {
  const isMobile = useIsMobile();

  return (
    <Layout.Column
      style={{ minHeight: '100vh', width: '100%' }}
      bg={isMobile ? 'white' : 'grayXXLight'}
    >
      <Layout.Row
        align
        justify="space-between"
        px={isMobile ? 16 : 32}
        py={isMobile ? 12 : 16}
        bg="white"
        style={{ borderBottom: '1px solid #ededed' }}
      >
        <Image
          source={require('../images/icons/logo.png')}
          style={{ width: isMobile ? 100 : 118, height: isMobile ? 36 : 42 }}
        />
        <Layout.Row align>
          <Icon name="checkmark-circle" size={14} color={theme.colors.successDark} />
          <Spacer.Horizontal size={6} />
          <OpenSans.Custom size="xs-12" weight="bold-700" color="successDark">
            {getTranslation('Trial started')}
          </OpenSans.Custom>
        </Layout.Row>
      </Layout.Row>

      <Layout.Column grow align px={isMobile ? 0 : 24} py={isMobile ? 0 : 40}>
        <Layout.Column
          align
          bg="white"
          radius={isMobile ? 0 : 20}
          px={isMobile ? 20 : 44}
          py={isMobile ? 32 : 44}
          border={isMobile ? undefined : [1, 'solid', 'grayXLight']}
          style={{ width: '100%', maxWidth: 560 }}
        >
          <Layout.Column
            align
            center
            bg="successXLight"
            radius={40}
            style={{ width: 80, height: 80 }}
          >
            <Icon name="checkmark" size={44} color={theme.colors.success} />
          </Layout.Column>

          <Spacer.Vertical size={20} />

          <OpenSans.Primary size={isMobile ? 'xl-28' : '2xl-32'} weight="bold-700" center>
            {getTranslation('Your Team plan is active')}
          </OpenSans.Primary>
          <Spacer.Vertical size={8} />
          <OpenSans.Secondary size="s-16" center>
            {getTranslation(
              'Your free trial has started. Paddle will email your confirmation shortly.',
            )}
          </OpenSans.Secondary>

          <Spacer.Vertical size={28} />

          <Layout.Column
            bg="grayXXLight"
            radius={16}
            px={isMobile ? 16 : 20}
            py={20}
            style={{ width: '100%' }}
          >
            <OpenSans.Secondary size="xs-12" weight="bold-700" style={{ letterSpacing: 1 }}>
              {getTranslation("WHAT'S NEXT")}
            </OpenSans.Secondary>
            <Spacer.Vertical size={16} />
            <NextStep icon="phone-portrait-outline" title={getTranslation('Return to the app')} />
            <Spacer.Vertical size={16} />
            <NextStep icon="people-outline" title={getTranslation('Invite your team')} />
            <Spacer.Vertical size={16} />
            <NextStep icon="receipt-outline" title={getTranslation('Start tracking together')} />
          </Layout.Column>

          <Spacer.Vertical size={28} />

          <OpenSans.Primary size="s-16" weight="bold-700" center>
            {getTranslation('You can close this tab and return to the app.')}
          </OpenSans.Primary>

          <Spacer.Vertical size={16} />

          <a
            href={TEAMS_FAQ_URL}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <Layout.Row align>
              <Icon name="help-circle-outline" size={16} color={theme.colors.brandPrimary} />
              <Spacer.Horizontal size={6} />
              <OpenSans.Pressable size="s-16">
                {getTranslation('Team subscriptions FAQ')}
              </OpenSans.Pressable>
            </Layout.Row>
          </a>
        </Layout.Column>
      </Layout.Column>

      <Layout.Row align center px={16} py={20}>
        <Icon name="shield-checkmark-outline" size={16} color={theme.colors.secondary} />
        <Spacer.Horizontal size={6} />
        <OpenSans.Secondary size="xs-12" center>
          {getTranslation('Payments are processed securely by Paddle. Cancel anytime.')}
        </OpenSans.Secondary>
      </Layout.Row>
    </Layout.Column>
  );
};
